<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { DncFieldMappingEnum } from '@/dnc/utils/dnc-field-mapping-enums';
import type { DncFieldMappingValue } from '@/dnc/utils/dnc-field-mapping-enums';
import { useI18N } from '@dialpad/i18n';
import SearchDropdown from '@/app/components/SearchDropdown/SearchDropdown.vue';
import type {
  Item,
  ItemGroup,
} from '@/app/components/SearchDropdown/SearchDropdown.vue';
import { DATA_QA } from './dnc-dialpad-property-select.constants';
import logger from '@/logger';

const { $t, $ta } = useI18N();

type Props = {
  required?: boolean;
  label?: string;
  disabled?: boolean;
  row?: number;
  excludedFields?: DncFieldMappingValue[];
};

const props = withDefaults(defineProps<Props>(), {
  required: false,
  label: '',
  disabled: false,
  row: 0,
  excludedFields: () => [],
});

const log = computed(() =>
  logger.getChild(`DncDialpadPropertySelect:${props.row}`),
);
log.value.debug('Mounting');

onMounted(() => {
  log.value.debug('Mounted');
});

const emit = defineEmits<(e: 'update:error', value: string | null) => void>();

const model = defineModel<DncFieldMappingValue | string | null>({
  default: null,
});

const CORE_DNC_PROPS: DncFieldMappingValue[] = [
  DncFieldMappingEnum.PhoneNumber,
  DncFieldMappingEnum.DoNotCall,
  DncFieldMappingEnum.DoNotText,
];

const DATE_PROPS: DncFieldMappingValue[] = [
  DncFieldMappingEnum.DoNotCallScope,
  DncFieldMappingEnum.DoNotCallExpiration,
  DncFieldMappingEnum.DoNotTextScope,
  DncFieldMappingEnum.DoNotTextExpiration,
];

const PHONE_METADATA_PROPS: DncFieldMappingValue[] = [
  DncFieldMappingEnum.Consent,
  DncFieldMappingEnum.Validity,
];

type CUSTOM_DNC_FIELDS = 'import_source' | 'notes' | 'campaign_id' | 'agent_id';
const CUSTOM_DNC_FIELDS_PROPS: CUSTOM_DNC_FIELDS[] = [
  'import_source',
  'notes',
  'campaign_id',
  'agent_id',
];

const value = ref<Item<string> | null>(null);
const fieldsI18n = $ta('DNC_DIALPAD_PROPERTY_SELECT');
const filter = ref('');

const fieldMap = <T extends string>(field: T): Item<T> => ({
  key: field,
  title: (fieldsI18n[field] as string | undefined) ?? field,
  ref: field,
});

const fieldFilter = (item: Item<string>) =>
  item.title.toLowerCase().includes(filter.value.toLowerCase()) &&
  (!props.excludedFields.includes(item.key as DncFieldMappingValue) ||
    item.key === model.value);

const groups = computed(() => {
  return [
    {
      title: $t('DNC_DIALPAD_PROPERTY_SELECT_CORE_DNC'),
      items: CORE_DNC_PROPS.map(fieldMap).filter(fieldFilter),
    } as ItemGroup<string>,
    {
      title: $t('DNC_DIALPAD_PROPERTY_SELECT_DATE_FIELDS'),
      items: DATE_PROPS.map(fieldMap).filter(fieldFilter),
    } as ItemGroup<string>,
    {
      title: $t('DNC_DIALPAD_PROPERTY_SELECT_PHONE_METADATA'),
      items: PHONE_METADATA_PROPS.map(fieldMap).filter(fieldFilter),
    } as ItemGroup<string>,
    {
      title: $t('DNC_DIALPAD_PROPERTY_SELECT_CUSTOM_FIELDS'),
      items: CUSTOM_DNC_FIELDS_PROPS.map(fieldMap).filter(fieldFilter),
    } as ItemGroup<string>,
  ];
});

const staticGroups = computed(() => {
  const doNotImportItem: Item<string> = {
    key: DncFieldMappingEnum.DoNotImport,
    title: fieldsI18n[DncFieldMappingEnum.DoNotImport],
    ref: DncFieldMappingEnum.DoNotImport,
  };

  return [{ items: [doNotImportItem] }] as ItemGroup<string>[];
});

watch(
  model,
  (newValue) => {
    log.value.debug('Model changed', newValue);
    if (newValue === null) {
      value.value = null;
      return;
    }

    const allItems: Item<string>[] = [
      ...staticGroups.value.flatMap((group) => group.items),
      ...groups.value.flatMap((group) => group.items),
    ];
    const foundItem = allItems.find((item) => item.ref === newValue);
    value.value = foundItem ?? null;
  },
  { immediate: true },
);

watch([value], () => {
  if (model.value === value.value?.ref) return;
  log.value.debug('Updating model', value.value);
  model.value = value.value?.ref ?? null;
});

const placeholder = computed(() => {
  if (props.disabled) {
    return $t('DNC_DIALPAD_PROPERTY_SELECT_DISABLED_PLACEHOLDER');
  }
  return $t('DNC_DIALPAD_PROPERTY_SELECT_PLACEHOLDER');
});
</script>
<template>
  <div :data-qa="DATA_QA.ROOT">
    <SearchDropdown
      v-model="value"
      :label="label"
      :messages="$ta('DNC_DIALPAD_PROPERTY_SELECT_MESSAGES')"
      :list-aria-label="$t('DNC_DIALPAD_PROPERTY_SELECT_LIST_ARIA_LABEL')"
      :groups="groups"
      :disabled="disabled"
      :static-groups="staticGroups"
      :debounce="0"
      :required="required"
      :placeholder="placeholder"
      :error-messages="$ta('DNC_DIALPAD_PROPERTY_SELECT_ERROR_MESSAGES')"
      @update:search="(value) => (filter = value)"
      @update:error="(value) => emit('update:error', value)"
    />
  </div>
</template>
